import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import api from "../../../services/api";
import { usePopup } from "../../common/Popup";
import PasswordInput from "../../common/PasswordInput";
import InfoTooltip from "../../common/InfoTooltip";
import cargoDescricoes from "../../../constants/cargoDescricoes";
import { cpf as validateCpf, phone as validateTelefone, email as validateEmail, normalizeTelefone } from "../../../utils/validation";
import { formatPhone } from "../../../utils/formatters";

const SquareContainer = styled.div`
  max-width: 800px;
  margin: 20px auto;
  padding: 40px 40px;
  background: #fff;
  border-radius: 28px;
  box-shadow: 2px 4px 4px #00000040;
  display: flex;
  gap: 40px;
  position: relative;
  top: 100px;
  z-index: 10;

  @media (max-width: 768px) {
    max-width: 90%;
    flex-direction: column;
    padding: 25px 20px;
    gap: 20px;
    margin: 60px auto 20px auto;
  }
`;

const LeftColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const RightColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const InputField = styled.input`
  padding: 10px;
  margin: 5px 0 15px 0;
  border: 0.5px solid #002E3B;
  border-radius: 9px;
  font-size: 14px;
  font-family: 'Public Sans';

  @media (max-width: 768px) {
    padding: 8px;
    margin: 5px 0 10px 0;
  }
`;

const Label = styled.label`
  font-size: 12px;
  color: #002E3B;
  font-weight: 500;
  font-family: 'Public Sans';
`;

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SenhaField = styled(PasswordInput)`
  margin: 5px 0 15px 0;

  @media (max-width: 768px) {
    margin: 5px 0 10px 0;
  }
`;

const RoleOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 10px;
`;

const RoleOption = styled.label`
  display: flex;
  align-items: center;
  padding: 10px;
  margin: 8px 0;
  border: 1px solid #FFCC85;
  border-radius: 9px;
  font-family: 'Public Sans';
  cursor: pointer;
  font-size: 14px;
`;

const SubmitButton = styled.button`
  width: 100%;
  max-width: 250px;
  height: 40px;
  background-color: #FF9F1F;
  color: white;
  font-size: 18px;
  font-weight: bold;
  border: none;
  border-radius: 9px;
  cursor: pointer;
  margin-top: 30px;
  align-self: center;

  &:hover {
    background-color: #E6900A;
  }

  @media (max-width: 768px) {
    height: 30px;
    margin-top: 15px;
  }
`;

const HiddenCheckbox = styled.input.attrs({ type: 'checkbox' })`
  position: absolute;
  opacity: 0;
  height: 0;
  width: 0;
`;

const CustomCheckbox = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid ${props => props.selected ? '#FF9F1F' : '#002E3B'};
  border-radius: 100px;
  margin-right: 12px;
  background-color: ${props => props.selected ? '#FF9F1F' : 'transparent'};
  flex-shrink: 0;
`;

const OptionText = styled.span`
  flex: 1;
`;

const SelectField = styled.select`
  padding: 10px;
  margin: 5px 0 15px 0;
  border: 1px solid #FFCC85;;
  border-radius: 9px;
  font-size: 14px;
  font-family: 'Public Sans';
  background-color: white;
  cursor: pointer;
`;

const FormComponent = () => {
  const navigate = useNavigate();
  const popup = usePopup();
  const [estabelecimentos, setEstabelecimentos] = useState([]);
  const [loadingEst, setLoadingEst] = useState(false);

  const [form, setForm] = useState({
    nome: "",
    email: "",
    cpf: "",
    senha: "",
    confirmarSenha: "",
    cargoId: null,
    telefone: "",
    endereco: "",
    numero: "",
    bairro: "",
    cidadeId: "",
    cep: "",
    estabelecimentoSaudeId: "",
  });

  const [selectedOption, setSelectedOption] = useState(null);
  useEffect(() => {
    if (selectedOption === 4 && estabelecimentos.length === 0) {
      const fetchEstabelecimentos = async () => {
        setLoadingEst(true);
        try {
          const data = await api.getEstabelecimentosSaude();
          setEstabelecimentos(data);
        } catch (error) {
          console.error("Erro ao carregar estabelecimentos:", error);
          popup.alert({
            icon: 'error',
            message: 'Não foi possível carregar a lista de estabelecimentos de saúde.',
          });
        } finally {
          setLoadingEst(false);
        }
      };
      fetchEstabelecimentos();
    }
  }, [selectedOption, estabelecimentos.length, popup]);

  const options = [
    // Cargo 1 fora do autocadastro: o /register só emite os cargos 2, 3 e 4, e
    // administrador é provisionado por "yarn criar-admin" no backend. Deixar a
    // opção aqui só rendia um 400 no envio.
    // { id: 1, label: "Gerenciador Geral dos Estágios", disabled: false },
    { id: 2, label: "Coordenador da Instituição de Ensino", disabled: false },
    { id: 3, label: "Coordenador de Estágio dos Cursos", disabled: false },
    { id: 4, label: "Gestor /colaborador NEPS/GEPS", disabled: false },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'cpf') {
      const digitsOnly = (value || '').replace(/\D/g, '').slice(0, 11);
      setForm({ ...form, cpf: digitsOnly });
      return;
    }
    if (name === 'nome') {
      const collapsed = (value || '').replace(/\s+/g, ' ');
      setForm({ ...form, nome: collapsed });
      return;
    }
    if (name === 'telefone') {
      setForm({ ...form, telefone: formatPhone(value) });
      return;
    }
    setForm({ ...form, [name]: value });
  };

  const isValidName = (nome) => {
    const trimmed = (nome || '').trim();
    if (trimmed.length < 3) return false;
    // allow letters (including accents), spaces and apostrophes/hyphen
    const invalidChars = /[^a-zA-ZÀ-ÿ'\-\s]/;
    return !invalidChars.test(trimmed);
  };

  const handleChangeOption = (id) => {
    const option = options.find(opt => opt.id === id);
    if (option && option.disabled) {
      return; // Impede a seleção de opções desabilitadas
    }
    setSelectedOption(id);
    setForm({ ...form, cargoId: id });
  };

  const handleSubmit = async () => {
    if (!isValidName(form.nome)) {
      await popup.alert({
        icon: 'warning',
        message: 'Informe um nome válido (mín. 3 letras, apenas letras e espaços).',
      });
      return;
    }

    if (!form.cpf || String(form.cpf).length !== 11) {
      await popup.alert({
        icon: 'warning',
        message: 'CPF deve conter exatamente 11 dígitos.',
      });
      return;
    }

    const cpfError = validateCpf(form.cpf);
    if (cpfError) {
      await popup.alert({
        icon: 'warning',
        message: cpfError,
      });
      return;
    }

    if (!form.cargoId) {
      await popup.alert({
        icon: 'warning',
        message: 'Selecione a sua função.',
      });
      return;
    }

    if (form.senha !== form.confirmarSenha) {
      await popup.alert({
        icon: 'warning',
        message: 'As senhas não coincidem.',
      });
      return;
    }

    const emailError = validateEmail(form.email);
    if (!form.email || emailError) {
      await popup.alert({
        icon: 'warning',
        message: emailError || 'Informe um e-mail válido.',
      });
      return;
    }

    const telefoneError = validateTelefone(form.telefone);
    if (!form.telefone || telefoneError) {
      await popup.alert({
        icon: 'warning',
        message: telefoneError || 'Informe um telefone válido.',
      });
      return;
    }

    if (form.cargoId === 3) {
      try {
        const coordenadorData = await api.buscarCoordenadorEstagio(form.cpf);
        if (!coordenadorData.encontrado) {
          await popup.alert({
            icon: 'warning',
            message: 'CPF não encontrado como coordenador de curso ou de estágio. Verifique se o CPF está correto ou se você já foi cadastrado como coordenador em algum curso.',
          });
          return;
        }
      } catch (error) {
        console.error('Erro ao validar coordenador de estágio:', error);
        await popup.alert({
          icon: 'error',
          message: 'Erro ao validar coordenador de estágio. Tente novamente.',
        });
        return;
      }
    }

    if (form.cargoId === 4 && !form.estabelecimentoSaudeId) {
      await popup.alert({
        icon: 'warning',
        message: 'Por favor, selecione o Estabelecimento de Saúde ao qual você pertence.',
      });
      return;
    }

    const telefonePayload = normalizeTelefone(form.telefone);

    try {
      const registerResponse = await api.register({
        nome: form.nome,
        email: form.email,
        cpf: form.cpf,
        senha: form.senha,
        cargoId: form.cargoId,
        telefone: telefonePayload,
        telefone2: null,
        endereco: form.endereco,
        numero: form.numero,
        bairro: form.bairro,
        cidadeId: Number(form.cidadeId),
        cep: form.cep,
        ...(form.cargoId === 4 && { estabelecimentoSaudeId: Number(form.estabelecimentoSaudeId) })
      });
    
      if (registerResponse) {
        try {
          const loginResponse = await api.login({
            emailCpf: form.email,
            senha: form.senha,
          });
    
          if (loginResponse && loginResponse.api_token) {
            localStorage.setItem("token", loginResponse.api_token);
            if (loginResponse.refreshTokenHandle) {
              localStorage.setItem("refreshTokenHandle", loginResponse.refreshTokenHandle);
            }
            localStorage.setItem("cargoId", form.cargoId);
    
            if (form.cargoId === 2) {
              await popup.alert({
                icon: 'success',
                message: 'Cadastro realizado com sucesso! Redirecionando...',
              });
              navigate('/Video');
            } else if (form.cargoId === 3) {
              await popup.alert({
                icon: 'success',
                message: 'Cadastro realizado com sucesso! Redirecionando...',
              });
              navigate('/VagasDisponibilizadas');
            } else {
              await popup.alert({
                icon: 'success',
                message: 'Cadastro realizado com sucesso! Aguarde aprovação do responsável ESP.',
              });
              navigate('/login');
            }
          }
        } catch (error) {
          await popup.alert({
            icon: 'success',
            message: 'Cadastro realizado com sucesso! Aguarde aprovação do responsável ESP.',
          });
          navigate('/login');
        }
      }
    } catch (error) {
      console.error("Erro no cadastro:", error);
      await popup.alert({
        icon: 'error',
        message: error.message || 'Erro ao cadastrar. Verifique os dados e tente novamente.',
      });
    }
  };

  return (
    <>
    <SquareContainer>
      <LeftColumn>
        <Label>Nome*</Label>
        <InputField name="nome" type="text" value={form.nome} onChange={handleChange} required />

        <Label>CPF*</Label>
        <InputField name="cpf" type="text" inputMode="numeric" value={form.cpf} onChange={handleChange} required />

        <LabelRow>
          <Label>Senha*</Label>
          <InfoTooltip
            label="Requisitos da senha"
            ariaLabel="Requisitos da senha"
          >
            <br />
            Mínimo de 8 caracteres, incluindo letra maiúscula, letra minúscula,
            número e caractere especial (ex.: ! @ # $ %).
          </InfoTooltip>
        </LabelRow>
        <SenhaField
          inputAs={InputField}
          hoverColor="#FF9F1F"
          name="senha"
          value={form.senha}
          onChange={handleChange}
          required
        />

        <Label>Confirmar senha*</Label>
        <SenhaField
          inputAs={InputField}
          hoverColor="#FF9F1F"
          name="confirmarSenha"
          value={form.confirmarSenha}
          onChange={handleChange}
          required
        />

        <Label>E-mail*</Label>
        <InputField name="email" type="email" value={form.email} onChange={handleChange} required />

        <Label>Telefone*</Label>
        <InputField name="telefone" type="tel" value={form.telefone} onChange={handleChange} required />
      </LeftColumn>

      <RightColumn>
        <Label><b>Selecione a sua função*</b></Label>
        <RoleOptionsContainer>
          {options.map((option) => (
            <React.Fragment key={option.id}>
              <RoleOption
                selected={selectedOption === option.id}
                onClick={() => handleChangeOption(option.id)}
              >
                <HiddenCheckbox
                  type="radio"
                  name="roleOption"
                  checked={selectedOption === option.id}
                  onChange={() => handleChangeOption(option.id)}
                />
                <CustomCheckbox selected={selectedOption === option.id} />
                <OptionText>{option.label}</OptionText>
                <InfoTooltip
                  label={option.label}
                  ariaLabel={`Sobre: ${option.label}`}
                >
                  {cargoDescricoes[option.id]}
                </InfoTooltip>
              </RoleOption>

              {/* Select condicional para NEPS/GEPS */}
              {option.id === 4 && selectedOption === 4 && (
                <>
                  <Label>Unidade de Saúde*</Label>
                  <SelectField 
                    name="estabelecimentoSaudeId" 
                    value={form.estabelecimentoSaudeId}
                    onChange={handleChange}
                    required
                  >
                    <option value="">{loadingEst ? 'Carregando...' : 'Selecione a unidade...'}</option>
                    {estabelecimentos.map(es => (
                      <option key={es.id} value={es.id}>{es.nome}</option>
                    ))}
                  </SelectField>
                </>
              )}
            </React.Fragment>
          ))}
        </RoleOptionsContainer>

        <SubmitButton type="button" onClick={handleSubmit}>Enviar</SubmitButton>
      </RightColumn>
    </SquareContainer>
    </>
  );
};

export default FormComponent;